import { format } from '@sequencehq/utils/dates'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import {
  CubeStatus,
  type CounterSigner,
  type QuoteContact
} from 'modules/Cube/domain/cube.domain.types'
import { validationResultSetHasErrors } from 'modules/Cube/domain/postActionStages/validation/cube.validation'
import { amendmentDateNotPassed } from 'modules/Cube/domain/postActionStages/validation/validators/amendmentDateNotPassed'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { match } from 'ts-pattern'
import { formatQuoteTitle } from 'Cube/utils/formatQuoteTitle'
import { getPortalDomain, useDisclosure } from '@sequencehq/utils'
import { getEnvironment } from 'lib/environment/environment'
import { usePermissions } from 'lib/rbac'
import { useEmbeddedSigningFlow } from '../hooks'
import { useQuoteApproval } from 'Cube/domain'
import {
  useSaveQuote,
  type QuoteExecuteScheduleAction
} from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving/useSaveQuote'
import { resolveNewQuoteProducts } from 'modules/Cube/communication/external/quotes.api.v1/ports/useSaveQuoteEditor'
import { useSaveProducts } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/entitySaving/useSaveProducts'
import { quoteAdapters } from 'modules/Cube/communication/external/quotes.api.v1/adapters/quote.adapters'
import { useNotifications } from '@sequencehq/core-components'
import { useQuoteSync, useWaitForQuoteSyncIdle } from '../sync'
import { usePublishButtonState } from './usePublishButtonState'
import type { PublishQuoteModalSubmitData } from './modals/PublishQuoteModalV2/types'

enum AvailableModal {
  Publish = 'publish', // @deprecated - use PublishEmbedded modal
  PublishEmbedded = 'publishEmbedded',
  Execute = 'execute'
}

export enum ViewMode {
  Edit = 'edit',
  Preview = 'preview'
}

type UseQuoteEditorHeader = () => {
  title: string
  isArchived: boolean
  syncing: boolean
  hasBeenPublished: boolean
  isReadyToSign: boolean
  portalUrl: string
  viewMode: {
    current: ViewMode
    onChange: (mode: string) => void
  }
  modals: {
    // @deprecated - use PublishEmbedded modal
    [AvailableModal.Publish]: {
      active: boolean
      onClose: () => void
      onConfirm: (data: {
        sendRecipientsEmail: boolean
        emailMessage: string | undefined
      }) => Promise<void>
      submitting: boolean
      recipientCountText: string
      isQuoteSyncing: boolean
    }
    [AvailableModal.PublishEmbedded]: {
      active: boolean
      onClose: () => void
      onConfirm: (data: PublishQuoteModalSubmitData) => Promise<void>
      submitting: boolean
      isQuoteSyncing: boolean
      isApprovalRequired: boolean
      /** Hold the modal on its spinner: we don't know what publishing does yet. */
      isSimulationPending: boolean
      /** The rules would turn this quote down, so there's nothing to confirm. */
      isAutoRejected: boolean
    }
    [AvailableModal.Execute]: {
      active: boolean
      onClose: () => void
      onConfirm: (result: {
        startDate: Date | undefined
        scheduleAction: QuoteExecuteScheduleAction
      }) => void
      submitting: boolean
      startDate: Date | undefined
    }
  }
  features: {
    publish: {
      visible: boolean
      disabled: boolean
      onClick: () => void
      text: string
      /** Show a spinner instead of the text: we've nothing to say yet. */
      isLoading: boolean
      /** Why the button can't be pressed, when the rules are what closed it. */
      tooltip: string | undefined
    }
    execute: {
      visible: boolean
      disabled: boolean
      onClick: () => void
      text: string
      tooltip: string | undefined
    }
    preview: {
      visible: boolean
      disabled: boolean
      onClick: () => void
    }
  }
  badgeUnpublishedChanges: {
    visible: boolean
  }
  badgeIsExpired: {
    visible: boolean
  }
  badgeNeedsReview: {
    visible: boolean
  }
  publishedLinkPopover: {
    isOpen: boolean
    onClose: () => void
  }
  quoteId: string
}

export const useQuoteEditorHeader: UseQuoteEditorHeader = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const cubeContext = useCubeContext()
  const saveQuote = useSaveQuote()
  const productsMutator = useSaveProducts()
  const { displayNotification } = useNotifications()
  const { can, roles } = usePermissions()
  const [activeModal, setActiveModal] = useState<AvailableModal | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const { status: quoteSyncStatus } = useQuoteSync()
  const waitForQuoteSyncIdle = useWaitForQuoteSyncIdle()
  const syncing = quoteSyncStatus === 'SYNCING'
  const isQuoteSyncing = quoteSyncStatus !== 'IDLE'
  const publishedLinkPopover = useDisclosure()
  const enablePublishedLinkPopover = useRef<boolean>(false)
  const { isEnabled: isEmbeddedSigningEnabled } = useEmbeddedSigningFlow()

  const onSelectPreview = useCallback(() => {
    const quoteId = cubeContext.queries.rawData.data.common.id
    if (quoteId) {
      navigate(`/quotes/${quoteId}/preview`)
    }
  }, [cubeContext.queries.rawData.data.common.id, navigate])

  const onSelectEdit = useCallback(() => {
    const quoteId = cubeContext.queries.rawData.data.common.id
    if (quoteId) {
      navigate(`/quotes/${quoteId}`)
    }
  }, [cubeContext.queries.rawData.data.common.id, navigate])

  const isPreviewMode = location.pathname.endsWith('/preview')

  const hasBeenPublished = useMemo(
    () => !!cubeContext.queries.rawData.data.quote.publishedAt,
    [cubeContext.queries.rawData.data.quote.publishedAt]
  )
  const hasUnpublishedChanges = useMemo(
    () =>
      cubeContext.queries.availableFeatures.quote.deleteLatestDraft.available
        .visible,
    [
      cubeContext.queries.availableFeatures.quote.deleteLatestDraft.available
        .visible
    ]
  )

  const isExpired = useMemo(
    () => cubeContext.queries.rawData.data.quote.isExpired,
    [cubeContext.queries.rawData.data.quote.isExpired]
  )

  const portalUrl = useMemo(() => {
    const { customerId, id } = cubeContext.queries.rawData.data.common
    return `${getPortalDomain(getEnvironment())}/quotes/${customerId}/${id}`
  }, [cubeContext.queries.rawData.data.common])

  const { isApprovalRequired, isSimulationPending, simulatedTerminalType } =
    useQuoteApproval()

  const modals = useMemo(() => {
    return {
      // @deprecated - use PublishEmbedded modal
      [AvailableModal.Publish]: {
        active: activeModal === AvailableModal.Publish,
        submitting: submitting || isQuoteSyncing,
        onClose: () => setActiveModal(null),
        onConfirm: async (data: {
          sendRecipientsEmail: boolean
          emailMessage: string | undefined
        }) => {
          setSubmitting(true)

          // Publishing sends the quote out, so it has to be the version the
          // user last saw - not whatever the auto-save was midway through.
          await waitForQuoteSyncIdle()

          const result = await cubeContext.mutators.external.out.quote?.publish(
            {
              recipients: undefined,
              sendRecipientsEmail: data.sendRecipientsEmail,
              emailMessage: data.emailMessage,
              makeReadyToSign: false,
              messageForApprover: undefined,
              awaitApprovalOutcome: !isApprovalRequired
            }
          )
          setSubmitting(false)
          setActiveModal(null)

          if (result && result.outcome === 'PUBLISHED') {
            enablePublishedLinkPopover.current = true
          }
        },
        recipientCountText: (() => {
          const { contacts } = cubeContext.queries.rawData.data.quote

          if (contacts.length === 1) {
            return '1 recipient'
          }

          return `${contacts.length} recipients`
        })(),
        isQuoteSyncing
      },
      [AvailableModal.PublishEmbedded]: {
        active: activeModal === AvailableModal.PublishEmbedded,
        // The button stays live during a save: publishing waits for the sync
        // itself, and a type change made in the modal shouldn't lock the
        // button someone is about to press.
        submitting,
        onClose: () => setActiveModal(null),
        onConfirm: async (data: PublishQuoteModalSubmitData) => {
          setSubmitting(true)

          // Publishing sends the quote out, so it has to be the version the
          // user last saw - not whatever the auto-save was midway through.
          await waitForQuoteSyncIdle()

          const result = await cubeContext.mutators.external.out.quote?.publish(
            {
              ...publishBodyFor(data, cubeContext.queries.rawData.data.quote),
              // Taken from what the modal said rather than by re-reading the
              // simulation, so the wait can't disagree with the button they
              // pressed.
              awaitApprovalOutcome: !isApprovalRequired
            }
          )

          setSubmitting(false)
          setActiveModal(null)

          if (result && result.outcome === 'PUBLISHED') {
            enablePublishedLinkPopover.current = true
          }
        },
        isQuoteSyncing,
        isApprovalRequired,
        isSimulationPending,
        isAutoRejected: simulatedTerminalType === 'END'
      },
      [AvailableModal.Execute]: {
        active: activeModal === AvailableModal.Execute,
        submitting,
        onClose: () => setActiveModal(null),
        startDate: cubeContext.queries.rawData.data.common.startDate,
        onConfirm: async ({
          startDate,
          scheduleAction
        }: {
          startDate: Date | undefined
          scheduleAction: QuoteExecuteScheduleAction
        }) => {
          const execute = cubeContext.mutators.external.out.quote?.execute
          const reload = cubeContext.mutators.external.in.core
          const quoteId = cubeContext.queries.rawData.data.common.id
          const existingStartDate =
            cubeContext.queries.rawData.data.common.startDate
          const domainData = cubeContext.queries.rawData.data
          setSubmitting(true)

          if (
            scheduleAction.mode === 'ADD_TO_EXISTING' &&
            quoteId !== 'uninitialized'
          ) {
            /**
             * Applying a renewal quote to an existing schedule. The renewal
             * date anchors the appended phases: a fixed-end schedule defaults to
             * the day after it ends (chaining straight on), but the user can pick
             * an earlier date to truncate the schedule there, and an open-ended
             * schedule requires an explicit date. Persist it to the quote first
             * (when it changed) so the appended phases anchor to it.
             */
            const startDateChanged =
              startDate && existingStartDate?.getTime() !== startDate.getTime()
            if (startDateChanged) {
              const { products, ...currentBody } = quoteAdapters.out(domainData)
              const resolvedBody = await resolveNewQuoteProducts(
                productsMutator,
                currentBody,
                products.new
              )

              if (resolvedBody === null) {
                displayNotification("Couldn't save new products", {
                  type: 'error'
                })
                setSubmitting(false)
                setActiveModal(null)
                return
              }

              await saveQuote.update(quoteId, {
                ...resolvedBody,
                preliminaryBillingStartDate: format(startDate, 'yyyy-MM-dd')
              })
            }
            const result = await saveQuote.execute(quoteId, scheduleAction)

            // Reload whichever way it went. A refusal usually means the quote
            // moved on behind us - a superseded amendment, say - and the editor
            // should show that rather than the state we tried to act on.
            await reload()

            if (result) {
              displayNotification('Schedule updated', { type: 'success' })
            } else {
              displayNotification("Couldn't update schedule", { type: 'error' })
            }
          } else if (startDate) {
            const startDateChanged =
              existingStartDate?.getTime() !== startDate.getTime()
            if (startDateChanged && quoteId !== 'uninitialized') {
              const { products, ...currentBody } = quoteAdapters.out(domainData)
              const resolvedBody = await resolveNewQuoteProducts(
                productsMutator,
                currentBody,
                products.new
              )

              if (resolvedBody === null) {
                displayNotification("Couldn't save new products", {
                  type: 'error'
                })
                setSubmitting(false)
                setActiveModal(null)
                return
              }

              await saveQuote.update(quoteId, {
                ...resolvedBody,
                preliminaryBillingStartDate: format(startDate, 'yyyy-MM-dd')
              })
              /**
               * The domain's executeQuote validates against in-memory state, which
               * still has no start date at this point. Bypass it and call the API
               * directly, then reload Cube - on success so the now-accepted quote
               * reflects the created schedule, and on failure because a refusal
               * usually means the quote moved on behind us. The domain's execute
               * path reloads either way too.
               */
              await saveQuote.execute(quoteId, scheduleAction)
              await reload()
            } else if (execute) {
              await execute(scheduleAction)
            }
          }
          setSubmitting(false)
          setActiveModal(null)
        }
      }
    }
  }, [
    activeModal,
    cubeContext,
    saveQuote,
    productsMutator,
    submitting,
    isQuoteSyncing,
    waitForQuoteSyncIdle,
    isApprovalRequired,
    isSimulationPending,
    simulatedTerminalType,
    displayNotification,
    roles
  ])

  const publishButtonText = useMemo(() => {
    if (isApprovalRequired) {
      return 'Send for review'
    }

    if (hasBeenPublished) {
      if (!hasUnpublishedChanges && isExpired) return 'Republish'

      return 'Publish changes'
    }

    return 'Publish'
  }, [hasBeenPublished, hasUnpublishedChanges, isExpired, isApprovalRequired])

  const publishButtonState = usePublishButtonState({
    fallbackText: publishButtonText
  })

  const executeResults =
    cubeContext.queries.validation.allValidationResults?.execute
  const hasNonStartDateExecuteErrors = executeResults
    ? validationResultSetHasErrors({
        ...executeResults,
        common: { ...executeResults.common, startDate: null }
      })
    : false

  const dealType = cubeContext.queries.rawData.data.quote.dealType
  const executeButtonText = match(dealType)
    .with('RENEWAL', 'AMENDMENT', () => 'Execute quote')
    .with('NEW_BUSINESS', () => 'Create billing schedule')
    .exhaustive()

  const isMissingAmendmentPermission =
    dealType === 'AMENDMENT' && !can('org.billing:update')

  const features = useMemo(() => {
    const {
      publish: publishFeature,
      execute: executeFeature,
      preview: previewFeature
    } = cubeContext.queries.availableFeatures.quote

    return {
      publish: {
        visible: publishFeature.available.visible,
        disabled:
          !publishFeature.available.enabled || publishButtonState.isDisabled,
        onClick: () => {
          // Validation only reruns when the editor changes, so a quote left open past midnight
          // could still think its amendment date is fine. We check the date again here, and the
          // update below reruns validation so the banner says why.
          const hasPublishErrors =
            cubeContext.queries.validation.validationErrorsPresent.publish ||
            Boolean(amendmentDateNotPassed(cubeContext.queries.rawData))

          if (hasPublishErrors) {
            cubeContext.mutators.updateEditor({
              activeValidationSet: 'publish'
            })
            return
          }

          if (isEmbeddedSigningEnabled) {
            setActiveModal(AvailableModal.PublishEmbedded)
          } else {
            setActiveModal(AvailableModal.Publish)
          }
        },
        text: publishButtonState.text,
        isLoading: publishButtonState.isLoading,
        tooltip: publishButtonState.tooltip
      },
      execute: {
        visible: executeFeature.available.visible,
        disabled:
          !executeFeature.available.enabled || isMissingAmendmentPermission,
        onClick: () => {
          if (hasNonStartDateExecuteErrors) {
            cubeContext.mutators.updateEditor({
              activeValidationSet: 'execute'
            })
            return
          }

          setActiveModal(AvailableModal.Execute)
        },
        text: executeButtonText,
        tooltip: isMissingAmendmentPermission
          ? 'You need permission to edit billing schedules to execute an amendment quote'
          : undefined
      },
      preview: {
        visible: previewFeature.available.visible,
        disabled: !previewFeature.available.enabled,
        onClick: onSelectPreview
      }
    }
  }, [
    cubeContext.queries.availableFeatures.quote,
    cubeContext.queries.validation.validationErrorsPresent.publish,
    cubeContext.queries.rawData,
    hasNonStartDateExecuteErrors,
    cubeContext.mutators,
    onSelectPreview,
    isEmbeddedSigningEnabled,
    publishButtonState,
    publishButtonText,
    executeButtonText,
    isMissingAmendmentPermission
  ])

  const badgeUnpublishedChanges = useMemo(
    () => ({
      visible: hasUnpublishedChanges
    }),
    [hasUnpublishedChanges]
  )

  const badgeIsExpired = useMemo(
    () => ({
      visible: isExpired
    }),
    [isExpired]
  )

  const { isApprovalPending } = useQuoteApproval()

  // Held back while the publish modal is up. The workflow starts the moment
  // it's submitted, and the modal then stays on "Publishing..." while we watch
  // it - so without this, "Waiting for review" would appear behind the modal
  // for a quote that is about to publish perfectly happily.
  const badgeNeedsReview = useMemo(
    () => ({ visible: isApprovalPending && activeModal === null }),
    [activeModal, isApprovalPending]
  )

  /**
   * Wait for the quote to become published after confirming the publish modal,
   * before showing the link popover
   */
  useEffect(() => {
    if (
      !enablePublishedLinkPopover.current ||
      cubeContext.queries.rawData.data.common.status !==
        CubeStatus.QuotePublished
    ) {
      return
    }

    publishedLinkPopover.onOpen()
    enablePublishedLinkPopover.current = false
  }, [cubeContext.queries.rawData.data.common.status, publishedLinkPopover])

  const canEditQuote = can('org.quote:update')

  return {
    title: formatQuoteTitle({
      domainTitle: cubeContext.queries.rawData.data.common.title,
      isArchived: cubeContext.queries.rawData.data.common.isArchived
    }),
    isArchived: cubeContext.queries.rawData.data.common.isArchived,
    syncing,
    hasBeenPublished,
    isReadyToSign: Boolean(
      cubeContext.queries.rawData.data.quote.readyToSignAt
    ),
    portalUrl,
    viewMode: {
      current: isPreviewMode ? ViewMode.Preview : ViewMode.Edit,
      onChange: (mode: string) => {
        if (!canEditQuote) {
          return
        }

        if (mode === ViewMode.Preview && isPreviewMode) return
        if (mode === ViewMode.Edit && !isPreviewMode) return

        if (mode === ViewMode.Preview) {
          onSelectPreview()
        } else {
          onSelectEdit()
        }
      }
    },
    modals,
    features,
    badgeUnpublishedChanges,
    badgeIsExpired,
    badgeNeedsReview,
    publishedLinkPopover: {
      isOpen: publishedLinkPopover.isOpen,
      onClose: publishedLinkPopover.onClose
    },
    quoteId: cubeContext.queries.rawData.data.common.id
  }
}

/**
 * The publish body for what the modal submitted.
 *
 * The rest of the body follows from the recipients picked: an email only goes
 * out if somebody was ticked, and the quote only becomes ready to sign if one
 * of them signs it - a cc-only selection leaves it published and nothing more.
 */
const publishBodyFor = (
  data: PublishQuoteModalSubmitData,
  quote: {
    contacts: QuoteContact[]
    counterSigners: CounterSigner[]
    isSignaturesEnabled: boolean
  }
) => {
  const signerEmails = new Set(
    [...quote.contacts, ...quote.counterSigners].map(signer => signer.email)
  )

  return {
    recipients: data.recipients,
    sendRecipientsEmail: data.recipients.length > 0,
    emailMessage: data.emailMessage,
    makeReadyToSign:
      quote.isSignaturesEnabled &&
      data.recipients.some(email => signerEmails.has(email)),
    messageForApprover: data.approvalComment
  }
}
